import { useCallback, useEffect, useState } from 'react';

/**
 * "Is there more below?" for a slide that scrolls.
 *
 * A cue that is wrong is worse than no cue — it teaches the reader to ignore it
 * — so this watches the events that actually move the number: scrolling, the
 * container resizing, a descendant finishing `load` (captured, because `load`
 * does not bubble), and web fonts arriving.
 *
 * A once-on-mount measurement is NOT enough, which is the trap the deleted
 * fitting hook fell into: a slide's height usually settles later, and an image
 * finishing its load grows a deep descendant's scroll area without changing the
 * border box of anything an observer is watching — so neither a ResizeObserver
 * nor a MutationObserver fires. Measured on a real image-text slide: 745px at
 * mount, 805px once settled.
 */
export function useScrollAffordance(
  ref: React.RefObject<HTMLElement | null>,
  enabled: boolean,
): boolean {
  const [hasMoreBelow, setHasMoreBelow] = useState(false);

  const sync = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    // 8px of slack: sub-pixel layout means scrollTop rarely lands exactly on
    // the end, and a cue that never quite clears is its own kind of wrong.
    const next = el.scrollHeight - el.scrollTop - el.clientHeight > 8;
    setHasMoreBelow((prev) => (prev === next ? prev : next));
  }, [ref]);

  useEffect(() => {
    if (!enabled) {
      setHasMoreBelow(false);
      return;
    }
    const el = ref.current;
    if (!el) return;

    let cancelled = false;
    const run = () => { if (!cancelled) sync(); };

    run();
    const raf = requestAnimationFrame(run);
    const observer = new ResizeObserver(run);
    observer.observe(el);
    el.addEventListener('scroll', run, { passive: true });
    el.addEventListener('load', run, true);
    document.fonts?.ready.then(run).catch(() => {});

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      observer.disconnect();
      el.removeEventListener('scroll', run);
      el.removeEventListener('load', run, true);
    };
  }, [enabled, sync, ref]);

  return hasMoreBelow;
}
